import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../../../shared/hooks/http-hook';

const PASSCODE_LENGTH = 4;

type InputState = 'default' | 'confirmed' | 'wrong';

interface SetPasscodeFormProps {
  handleContinue: (code: string) => void;
  errorMessage?: string;
}

export function SetPasscodeForm({ handleContinue, errorMessage }: SetPasscodeFormProps) {
  const { isLoading } = useHttpClient();
  const { t } = useTranslation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');
  const [error, setError] = useState('');
  const [inputState, setInputState] = useState<InputState>('default');
  const [confirmInputState, setConfirmInputState] = useState<InputState>('default');

  const isValidPasscode = passcode.length === PASSCODE_LENGTH;
  const isValidConfirmPasscode = confirmPasscode.length === PASSCODE_LENGTH;
  const passcodesMatch = passcode === confirmPasscode;
  const isButtonAccepted =
    isValidPasscode &&
    isValidConfirmPasscode &&
    inputState !== 'wrong' &&
    confirmInputState !== 'wrong';

  const handlePasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isNumeric = /^\d*$/.test(value);

    if (isNumeric && value.length <= PASSCODE_LENGTH) {
      setPasscode(value);
      setError('');
      setInputState('default');
      setConfirmInputState('default');
    }
  };

  const handleConfirmPasscodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isNumeric = /^\d*$/.test(value);

    if (isNumeric && value.length <= PASSCODE_LENGTH) {
      setConfirmPasscode(value);
      setError('');
      setConfirmInputState('default');
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!isValidPasscode || !isValidConfirmPasscode) {
      setError(t('signin.passcode.error'));
      return;
    }

    if (passcodesMatch) {
      setInputState('confirmed');
      setConfirmInputState('confirmed');
      timeoutRef.current = setTimeout(() => {
        handleContinue(passcode);
      }, 2000);
    } else {
      setError(t('signup.passcode.does_not_match'));
      setConfirmInputState('wrong');
    }
  };

  useEffect(() => {
    if (errorMessage && errorMessage.length > 0) {
      setError(errorMessage);
      timeoutRef.current = setTimeout(() => {
        setPasscode('');
        setConfirmPasscode('');
      }, 2000);
    }
  }, [errorMessage]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <label className="not_logged_label" htmlFor="passcode">
        {t('signup.passcode.label')}
      </label>
      <div className="passcode">
        <input
          type="password"
          className={`code_input passcode ${inputState}`}
          id="passcode"
          name="passcode"
          minLength={PASSCODE_LENGTH}
          maxLength={PASSCODE_LENGTH}
          onChange={handlePasscodeChange}
          value={passcode}
          aria-label={t('signup.passcode.label')}
          data-testid="passcode-input"
        />
      </div>
      <label className="not_logged_label" htmlFor="confirm-passcode">
        {t('signup.passcode.confirm_label')}
      </label>
      <div className="passcode">
        <input
          type="password"
          className={`code_input passcode ${confirmInputState}`}
          id="confirm-passcode"
          name="confirm-passcode"
          minLength={PASSCODE_LENGTH}
          maxLength={PASSCODE_LENGTH}
          onChange={handleConfirmPasscodeChange}
          value={confirmPasscode}
          aria-label={t('signup.passcode.confirm_label')}
          data-testid="confirm-passcode-input"
        />
      </div>
      {isLoading && <LoadingSpinner asOverlay />}
      <Alert
        type="error"
        message={error}
        id="passcode-error"
        dataTestId="passcode-error"
        isVisible={!!error}
        isAssessive
      />
      {!isLoading && (
        <Button
          type="submit"
          id="continue"
          className={isButtonAccepted ? 'accepted' : 'declined'}
          dataTestId="passcode-submit-button"
          formNoValidate
        >
          {t('common.continue')}
        </Button>
      )}
    </form>
  );
}
